import React, { useState, useRef, useEffect } from 'react';
import './PhotoUploader.css';

const PhotoUploader = () => {
  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [isIOS, setIsIOS] = useState(false);

  // Detect iOS on component mount
  useEffect(() => {
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) || 
             (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  }, []);

  // ========== IMAGE HANDLING FUNCTIONS ==========
  const clearAllImages = () => {
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setImages([]);
    setPreviewUrls([]);
    localStorage.removeItem('uploadedImages');
  };

  const removeImage = (index) => {
    const newImages = [...images];
    const newPreviewUrls = [...previewUrls];
    
    URL.revokeObjectURL(newPreviewUrls[index]);
    newImages.splice(index, 1);
    newPreviewUrls.splice(index, 1);
    
    setImages(newImages);
    setPreviewUrls(newPreviewUrls);
    localStorage.setItem('uploadedImages', JSON.stringify(newImages));
  };

  const processFiles = async (files) => {
    const validFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (validFiles.length === 0) {
      alert('Please select valid image files only.');
      return;
    }

    setLoading(true);
    try {
      const newImages = [];
      const newPreviewUrls = [];
      
      for (const file of validFiles) {
        // iOS Safari workaround for image orientation
        const processedFile = await fixIOSImageOrientation(file);
        const url = URL.createObjectURL(processedFile);
        
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(processedFile);
        });
        
        newImages.push({
          name: processedFile.name,
          type: processedFile.type,
          size: processedFile.size,
          lastModified: processedFile.lastModified,
          base64,
          url
        });
        newPreviewUrls.push(url);
      }

      setImages(prev => [...prev, ...newImages]);
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
      localStorage.setItem('uploadedImages', JSON.stringify([...images, ...newImages]));
    } catch (error) {
      console.error('Error processing files:', error);
      alert('Error processing images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fix iOS image orientation issues
  const fixIOSImageOrientation = async (file) => {
    if (!isIOS) return file;
    
    try {
      const image = await createImageBitmap(file);
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0);
      
      return await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          }));
        }, file.type, 0.95);
      });
    } catch (error) {
      console.error('Error fixing orientation:', error);
      return file;
    }
  };

  // ========== DRAG & DROP HANDLERS ==========
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    e.target.value = '';
  };

  // ========== CAMERA FUNCTIONS ==========
  const startCamera = async () => {
    try {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      // iOS-specific constraints
      const constraints = {
        video: { 
          facingMode,
          width: { ideal: isIOS ? 1920 : 1280 },
          height: { ideal: isIOS ? 1080 : 720 },
          // iOS-specific optimizations
          frameRate: { ideal: 30 },
          resizeMode: 'crop-and-scale'
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints)
        .catch(err => {
          // Fallback for older iOS versions
          if (isIOS) {
            return navigator.mediaDevices.getUserMedia({
              video: { facingMode },
              audio: false
            });
          }
          throw err;
        });

      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // iOS requires this for proper video playback
        videoRef.current.playsInline = true;
        videoRef.current.setAttribute('playsinline', 'true');
      }
      setShowCamera(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Could not access the camera. Please check permissions.");
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const capturePhoto = (e) => {
    e.preventDefault();
    if (!videoRef.current || !cameraStream) return;

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    
    // iOS-specific drawing fix
    if (isIOS) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `camera-photo-${Date.now()}.jpg`, {
        type: 'image/jpeg'
      });
      processFiles([file]);
    }, 'image/jpeg', 0.95);
  };

  // ========== LIFECYCLE EFFECTS ==========
  useEffect(() => {
    const loadImages = async () => {
      const savedImages = localStorage.getItem('uploadedImages');
      if (!savedImages) return;

      setLoading(true);
      try {
        const parsedImages = JSON.parse(savedImages);
        const loadedImages = [];
        const loadedPreviewUrls = [];
        
        for (const image of parsedImages) {
          try {
            if (image.base64) {
              const blob = await fetch(`data:${image.type};base64,${image.base64}`)
                .then(res => res.blob());
              const url = URL.createObjectURL(blob);
              loadedImages.push({ ...image, url });
              loadedPreviewUrls.push(url);
            }
          } catch (e) {
            console.warn('Failed to load stored image', e);
          }
        }

        setImages(loadedImages);
        setPreviewUrls(loadedPreviewUrls);
        if (loadedImages.length !== parsedImages.length) {
          localStorage.setItem('uploadedImages', JSON.stringify(loadedImages));
        }
      } catch (error) {
        console.error('Error loading images:', error);
        localStorage.removeItem('uploadedImages');
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, []);

  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [previewUrls, cameraStream]);

  useEffect(() => {
    if (showCamera) {
      startCamera();
    }
    return () => {
      if (showCamera && cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [facingMode, showCamera]);

  return (
    <div className="photo-uploader">
      <h2>Upload Photos</h2>
      
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          Loading images...
        </div>
      )}
      
      {!showCamera ? (
        <>
          <div 
            className="drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current.click()}
          >
            <p>Drag & drop photos here or click to select</p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              multiple
              capture={isIOS ? "environment" : undefined} // iOS specific
              style={{ display: 'none' }}
            />
          </div>
          
          {(navigator.mediaDevices?.getUserMedia) && (
            <button 
              onClick={startCamera}
              className="camera-button"
              type="button"
            >
              {isIOS ? "Take Photo" : "Take Photo with Camera"}
            </button>
          )}
        </>
      ) : (
        <div className="camera-container">
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline
            muted
            className="camera-view"
            style={{
              transform: isIOS && facingMode === 'user' ? 'scaleX(-1)' : 'none'
            }}
          />
          <div className="camera-controls">
            <button 
              onClick={capturePhoto} 
              className="capture-button"
              type="button"
            >
              Capture Photo
            </button>
            {navigator.mediaDevices.getSupportedConstraints().facingMode && (
              <button 
                onClick={toggleCamera}
                className="toggle-camera-button"
                type="button"
              >
                Switch Camera
              </button>
            )}
            <button 
              onClick={stopCamera} 
              className="cancel-button"
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {previewUrls.length > 0 && (
        <div className="preview-container">
          <div className="preview-header">
            <h3>Selected Photos ({previewUrls.length})</h3>
            <button 
              onClick={clearAllImages}
              className="clear-all-button"
              type="button"
            >
              Clear All
            </button>
          </div>
          <div className="preview-grid">
            {previewUrls.map((url, index) => (
              <div key={index} className="preview-item">
                <img 
                  src={url} 
                  alt={`Preview ${index}`} 
                  style={{
                    transform: images[index]?.facingMode === 'user' ? 'scaleX(-1)' : 'none'
                  }}
                  onError={(e) => {
                    e.target.src = '/image-placeholder.png';
                    console.error('Failed to load image:', url);
                  }}
                />
                <button 
                  onClick={() => removeImage(index)}
                  className="remove-button"
                  type="button"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;